import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Files, Sparkles } from "lucide-react";
import { prizeLogicActions } from "../../config/navigation";
import { useModuleAccess } from "../../hooks/useModuleAccess";
import { getKbGeneratedDocumentsSummary } from "../../services/gameRuleGeneratedDocumentsService";

const iconConfig: Record<
  string,
  {
    bg: string;
    text: string;
    icon: React.ReactNode;
  }
> = {
  create: {
    bg: "bg-menu-upload",
    text: "text-violet-600",
    icon: <FileText className="h-6 w-6" />,
  },
  generate: {
    bg: "bg-menu-list",
    text: "text-primary",
    icon: <Sparkles className="h-6 w-6" />,
  },
  documents: {
    bg: "bg-menu-library",
    text: "text-emerald-600",
    icon: <Files className="h-6 w-6" />,
  },
};

export const PrizeLogicMenu = () => {
  const navigate = useNavigate();
  const { canPrizeRules } = useModuleAccess();
  const [generatedDocumentCount, setGeneratedDocumentCount] = useState<number | null>(null);

  useEffect(() => {
    if (!canPrizeRules) {
      return;
    }

    let cancelled = false;
    void getKbGeneratedDocumentsSummary()
      .then((total) => {
        if (!cancelled) {
          setGeneratedDocumentCount(total);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGeneratedDocumentCount(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canPrizeRules]);

  if (!canPrizeRules) {
    return (
      <p className="text-sm text-muted-foreground">
        You do not have access to PrizeLogic. Contact your administrator if you need access.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {prizeLogicActions.map((card) => {
          const cfg = iconConfig[card.id] ?? iconConfig.create;
          const showCount = card.id === "documents" && generatedDocumentCount !== null;

          return (
            <button
              key={card.id}
              type="button"
              onClick={() => navigate(card.route)}
              className="flex w-full items-center gap-4 rounded-xl border border-[var(--border)] bg-card p-4 text-left shadow-sm transition-colors hover:border-primary hover:bg-accent"
            >
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${cfg.bg} ${cfg.text}`}>
                {cfg.icon}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-foreground">{card.title}</p>
                <p className="text-sm text-muted-foreground">{card.description}</p>
                {showCount ? (
                  <p className="mt-1 text-xs font-medium text-muted-foreground">
                    {generatedDocumentCount}
                    {generatedDocumentCount === 1 ? " document" : " documents"}
                  </p>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
